'use client'

import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog'
import { cn, formatMoney } from '@/lib/utils'
import { IReferralsStatsBucket, IReferralsStats } from '@/types/referrals.types'
import { ChevronRight, Repeat, X, Zap } from 'lucide-react'
import { InfoTooltip } from './info-tooltip'
import { StatTile } from './stat-tile'

const ACCENT = {
  blue: {
    border: 'border-blue-500/30',
    bg: 'bg-blue-500/5',
    badgeBg: 'bg-blue-500/15',
    badgeText: 'text-blue-300',
    amount: 'text-blue-300',
  },
  purple: {
    border: 'border-purple-500/30',
    bg: 'bg-purple-500/5',
    badgeBg: 'bg-purple-500/15',
    badgeText: 'text-purple-300',
    amount: 'text-purple-300',
  },
} as const

function BucketPanel({
  index,
  accent,
  icon,
  title,
  description,
  bucket,
}: {
  index: number
  accent: keyof typeof ACCENT
  icon: React.ReactNode
  title: string
  description: string
  bucket: IReferralsStatsBucket
}) {
  const a = ACCENT[accent]
  return (
    <div className={cn('rounded-xl border p-5', a.border, a.bg)}>
      <div className="flex items-center gap-3">
        <div
          className={cn(
            'flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-base font-bold',
            a.badgeBg,
            a.badgeText
          )}
        >
          {index}
        </div>
        <div className="min-w-0">
          <div className="flex items-center gap-2 text-base font-semibold text-gray-100 lg:text-lg">
            {icon}
            {title}
          </div>
          <p className="mt-0.5 text-sm text-gray-400">{description}</p>
        </div>
      </div>
      <div className="mt-4 grid grid-cols-3 gap-2 text-center">
        <div>
          <p className={cn('text-lg font-bold lg:text-xl', a.amount)}>
            {formatMoney(bucket.amount)} ₽
          </p>
          <p className="mt-0.5 text-xs text-gray-400">Начислено</p>
        </div>
        <div>
          <p className="text-lg font-bold text-gray-100 lg:text-xl">{bucket.count}</p>
          <p className="mt-0.5 text-xs text-gray-400">Оплат</p>
        </div>
        <div>
          <p className="text-lg font-bold text-gray-100 lg:text-xl">{bucket.referrals}</p>
          <p className="mt-0.5 text-xs text-gray-400">Рефералов</p>
        </div>
      </div>
    </div>
  )
}

/** Разворачивает сводные плашки реф-программы в детальную разбивку по первичным/вторичным оплатам. */
export function ReferralStatsDetailsDialog({ stats }: { stats?: IReferralsStats }) {
  if (!stats) return null

  return (
    <Dialog>
      <DialogTrigger asChild>
        <button
          type="button"
          className="mx-auto flex items-center gap-1.5 rounded-full border border-white/10 bg-white/5 px-4 py-1.5 text-xs text-gray-300 transition-colors hover:border-white/20 hover:bg-white/10 hover:text-white lg:text-sm"
        >
          Подробная статистика
          <ChevronRight className="h-3.5 w-3.5" />
        </button>
      </DialogTrigger>

      <DialogContent
        showClose={false}
        variant="full-on-mobile"
        className="overflow-y-auto border-gray-700 bg-gray-900 sm:max-h-[85vh] sm:w-[calc(100%-2rem)] sm:max-w-2xl"
      >
        <div className="flex items-start justify-between gap-4">
          <div>
            <DialogTitle className="bg-gradient-to-r from-blue-400 to-purple-400 bg-clip-text text-2xl font-bold text-transparent lg:text-3xl">
              Статистика по рефералам
            </DialogTitle>
            <p className="mt-1.5 text-base text-gray-400">
              Как начисляются бонусы за приглашённых
            </p>
          </div>
          <DialogClose
            aria-label="Закрыть"
            className="shrink-0 rounded-sm text-white/70 opacity-70 transition-opacity hover:text-white hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-white/50 focus:ring-offset-2 focus:ring-offset-gray-900 disabled:pointer-events-none"
          >
            <X className="h-6 w-6" />
          </DialogClose>
        </div>

        <div className="grid grid-cols-3 gap-1.5 rounded-xl border border-white/5 bg-black/20 px-2 py-4 sm:gap-4 sm:px-4 sm:py-5">
          <StatTile
            value={`${formatMoney(stats.total_earned)} ₽`}
            label="Начислено в баланс"
            gradient="from-green-400 to-blue-400"
            size="md"
          />
          <StatTile
            value={`${formatMoney(stats.total_accruals)} ₽`}
            label="Начислений всего"
            tooltip={<InfoTooltip text="Сумма всех начислений от оплат приглашённых за всё время" />}
            plain
            size="md"
          />
          <StatTile
            value={stats.paying_referrals}
            label="Платящих"
            tooltip={<InfoTooltip text="Уникальные приглашённые, которые хотя бы раз оплатили подписку" />}
            plain
            size="md"
          />
        </div>

        <div className="space-y-3">
          <BucketPanel
            index={1}
            accent="blue"
            icon={<Zap className="h-5 w-5 shrink-0 text-blue-400" />}
            title="Первичные оплаты"
            description="Первая оплата приглашённого — тариф на 1 сутки"
            bucket={stats.primary}
          />
          <BucketPanel
            index={2}
            accent="purple"
            icon={<Repeat className="h-5 w-5 shrink-0 text-purple-400" />}
            title="Вторичные оплаты"
            description="Все последующие оплаты — кроме тарифа на 1 сутки"
            bucket={stats.secondary}
          />
        </div>
      </DialogContent>
    </Dialog>
  )
}
